import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  signOut,
  createUserWithEmailAndPassword,
  updateProfile,
  browserLocalPersistence,
  User as FirebaseUser,
} from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'
import { RolesMap, UserClass } from '@/models/user'
import FirebaseService from './FirebaseService'
import { createGlobalState, useStorage } from '@vueuse/core'
import { computed } from 'vue'
import { doc, getDoc } from 'firebase/firestore'
import { App } from 'vue'

export class AuthService {
  private static store = createGlobalState(
    () => {
      const user = useStorage(
        'auth-user',
        new UserClass(),
        localStorage,
        { mergeDefaults: true }
      )
      const currentUser = computed(() => user.value)
      const updateCurrentUser = (newUser: UserClass) => user.value = Object.assign(user.value, newUser)

      return { currentUser, updateCurrentUser }
    }
  )

  async loginOrSignUpGoogle() {
    const auth = await FirebaseService.auth()
    const provider = new GoogleAuthProvider()


    auth.setPersistence(browserLocalPersistence)
    provider.addScope('profile')
    provider.addScope('email')
  
    await signInWithPopup(auth, provider)
  }

  async loginEmail(username: string, pass: string) {
    const auth = await FirebaseService.auth()

    auth.setPersistence(browserLocalPersistence)

    await signInWithEmailAndPassword(auth, username, pass)
  }

  async logout() {
    const auth = await FirebaseService.auth()
    await signOut(auth)
    AuthService.store().updateCurrentUser(new UserClass())
  }

  async registerEmailUser (email: string, password: string, name: string) {
    const auth = await FirebaseService.auth()
   
    auth.setPersistence(browserLocalPersistence)
   
    const userCredential = await createUserWithEmailAndPassword(auth, email, password)
    await updateProfile(userCredential.user, { displayName: name })
  }

  async sendVerificationEmail (email: string, redirectURL: string) {
    const functions = await FirebaseService.functions()
    const sendEmail = httpsCallable(functions, 'auth-sendVerificationEmailV2')
    await sendEmail({ email: email, redirectURL: redirectURL })
  }

  async fetcUserProfile(authUser: FirebaseUser) {
    const db = await FirebaseService.firestore()
    const profileRef = doc(db, 'users', authUser.uid)
    const profile = (await getDoc(profileRef)).data() || {}
    const claims = (await authUser.getIdTokenResult(true)).claims

    return Object.assign(profile, { roles: claims })
  }

  async syncProfile(authUser: FirebaseUser) {
    const profile = await this.fetcUserProfile(authUser)
    const user = Object.assign(new UserClass(), {
      id: authUser.uid,
      name: profile.name || authUser.displayName,
      email: profile.email || authUser.email,
      phone: profile.phone || authUser.phoneNumber,
      avatar: profile.avatar || authUser.photoURL,
      hubspotId: profile.hubspotId || null,
      address: profile.address || null,
      billing: profile.billing || null,
      verified: authUser.emailVerified,
      roles: profile.roles,
    })
    
    AuthService.store().updateCurrentUser(user)
    return AuthService.store().currentUser.value
  }

  get currentUser() {
    return AuthService.store().currentUser.value
  }

  get isAuthenticated() {
    const user = this.currentUser
    return !!user.id
  }

  get isAdmin() {
    const user = this.currentUser
    return !!user.roles[RolesMap.ADMIN_ROLE]
  }

  get isEmployee() {
    const user = this.currentUser
    return !!user.roles[RolesMap.EMPLOYEE_ROLE]
  }

  get isSales() {
    const user = this.currentUser
    return !!user.roles[RolesMap.SALES_ROLE]
  }

  get isProjectManager() {
    const user = this.currentUser
    return !!user.roles[RolesMap.PROJECT_MANAGER_ROLE]
  }

  get isEstimator() {
    const user = this.currentUser
    return !!user.roles[RolesMap.ESTIMATOR_ROLE]
  }

  get isFinance() {
    const user = this.currentUser
    return !!user.roles[RolesMap.FINANCE_ROLE]
  }

  get isSubcontractor() {
    const user = this.currentUser
    return !!user.roles[RolesMap.SUBCONTRACTOR_ROLE]
  }

  get isCustomer() {
    const user = this.currentUser
    return !!user.roles[RolesMap.CUSTOMER_ROLE]
  }

  get isVerified() {
    const user = this.currentUser
    return user.verified
  }

  get userRoles() {
    const user = this.currentUser
    if (!user.roles) return []

    return Object.keys(user.roles).filter(role => user.roles[role] === true)
  }
}

type AuthServicePluginOptions = {
  authService: AuthService
}
export const AuthServicePlugin = {
  install: (app: App, options: AuthServicePluginOptions) => { 
    const authService = options.authService ? options.authService : new AuthService()
    app.config.globalProperties.$auth = authService
    app.provide('authService', authService)
  }
}